import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import CompaniesTable from './CompaniesTable';
import CompaniesTableMobile from './CompaniesTableMobile';
import CompanyModal from './CompanyModal';
import ManageCountriesModal from './ManageCountriesModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';

import {
  fetchCompanies,
  updateCompany,
  createCompany,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './companies.scss';

const CompaniesTableContainer = (props) => {
  const {
    companies = [],
    distributors,
    createCompany,
    updateCompany,
    deleteCompany,
    fetchCompanies,
    withButton,
    toggleSidebar,
    countries,
    manufacturers = [],
    logistics = [],
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [showManageCountriesModal, toggleManageCountriesModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const onEditClick = (company) => {
    setSelectedCompany(company);
    toggleUpdateModal(true);
  };

  const onManageCountriesClick = (company) => {
    setSelectedCompany(company);
    toggleManageCountriesModal(true);
  };

  const onCompanyCreate = async (formObj) => {
    try {
      setLoading(true);
      await createCompany(formObj);
      await fetchCompanies();
      setSuccessMessage('New company successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onCompanyUpdate = async (formObj) => {
    if (selectedCompany) {
      try {
        setLoading(true);

        await updateCompany(selectedCompany?.id, formObj);
        await fetchCompanies();
        setSuccessMessage('Company successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onManageCountriesSubmit = async (countryConfig) => {
    if (selectedCompany) {
      try {
        setLoading(true);
        await updateCompany(selectedCompany?.id, { countryConfig });
        await fetchCompanies();
        setSuccessMessage('Company successfully updated');
        toggleManageCountriesModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteCompany = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteCompany(selectedId);
        await fetchCompanies();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const filteredCompanies = useMemo(() => {
    let filtered = companies;

    if (search) {
      const q = search.toLowerCase();
      filtered = companies.filter((p) => p?.name?.toLowerCase()?.includes(q));
    }

    return filtered;
  }, [companies, search]);

  const list = useMemo(() => {
    let result = filteredCompanies;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredCompanies, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <IconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Companies
                  <Box className="badge">{companies.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage companies and their branding or contacts
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <Button
              text='Add Company'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {companies.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <CompaniesTable
                companies={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onManageCountriesClick={onManageCountriesClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredCompanies?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <CompaniesTableMobile companies={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Companies</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteCompany}
        title="Delete Company"
        text="Are you sure you want to delete this company?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <CompanyModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onCompanyCreate}
        countriesList={countries}
        loading={loading}
      />

      <CompanyModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedCompany(undefined);
        }}
        onSubmit={onCompanyUpdate}
        countriesList={countries}
        mode='update'
        initialValues={selectedCompany}
        loading={loading}
      />

      <ManageCountriesModal
        open={showManageCountriesModal}
        onClose={() => {
          toggleManageCountriesModal(false);
          setSelectedCompany(undefined);
        }}
        company={selectedCompany}
        onSubmit={onManageCountriesSubmit}
        manufacturers={manufacturers}
        logistics={logistics}
        loading={loading}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteCompany}
        title="Delete Company?"
        text="Are you sure you want to delete this company? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

CompaniesTableContainer.propTypes = {
  companies: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    companyTags: state.companies.tags,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchCompanies: () => dispatch(fetchCompanies()),
    createCompany: (companyData) => dispatch(createCompany(companyData)),
    updateCompany: (id, companyData) => dispatch(updateCompany(id, companyData)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesTableContainer);
